/**
 * Outline Generation Store
 *
 * Background-job state for AI outline generation. The SSE stream is driven by
 * a *module-level* singleton (outside React) so generation survives navigation
 * — the user can leave /tutor/outlines and the stream keeps going.
 *
 * Why a module-level runner instead of a hook: the previous implementation
 * lived inside the OutlineList component, so unmounting that page aborted the
 * AbortController and killed the stream. Pulling the runner out of React fixes
 * that.
 *
 * @author Tutara Team
 * @since 2026-05 (non-blocking outline generation)
 */

import { create } from 'zustand';
import {
  streamGenerate,
  type StreamGenerateRequest,
  type OutlineSseEvent,
} from '@/services/outline/outlineSse';
import { toast } from '@/utils/toast';

// =============================================================================
// Types
// =============================================================================

export type OutlineGenerationStatus =
  | 'idle'
  | 'streaming'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type OutlineGenerationErrorCode =
  | 'CONNECTION' // network/connection dropped
  | 'TIMEOUT'    // backend hung
  | 'AGENT'      // backend reported an agent-side error event
  | 'OTHER';

/** Inputs the user filled into the "Generate outline with AI" form. The
 * optional fields mirror `CreateOutlineRequest` / `StreamGenerateRequest`
 * upstream — the modal validates topic + audience as non-empty before
 * submit, but the static type stays optional to match. */
export interface OutlineGenerationFormData {
  topic: string;
  audience?: string;
  difficulty?: string;
  language: string;
  targetRole?: string;
  roughStructure?: string;
}

interface OutlineGenerationState {
  status: OutlineGenerationStatus;
  topic: string | null;
  statusMessage: string | null;
  errorMessage: string | null;
  errorCode: OutlineGenerationErrorCode | null;
  retryCount: number;
  formData: OutlineGenerationFormData | null;

  /** Bumped each time a generation persists; React listeners watch this to
   * invalidate the outlines list query without needing a custom event bus. */
  persistedTick: number;
  lastPersistedOutlineId: string | null;
  lastPersistedTopic: string | null;

  // Actions
  start: (formData: OutlineGenerationFormData) => void;
  cancel: () => void;
  retry: () => void;
  /** Reset to idle (clears completed/failed state). */
  dismiss: () => void;
}

const MAX_RETRY_ATTEMPTS = 3;

// =============================================================================
// Module-level SSE runner (survives React unmounts)
// =============================================================================

let activeController: AbortController | null = null;

/**
 * Drive the SSE stream for a given form submission. Mutates the store as
 * events arrive. Resolves when the stream ends (success, failure, or abort).
 */
async function runOutlineSse(formData: OutlineGenerationFormData): Promise<void> {
  const controller = new AbortController();
  activeController = controller;

  const request: StreamGenerateRequest = {
    topic: formData.topic,
    audience: formData.audience,
    difficulty: formData.difficulty,
    language: formData.language,
    targetRole: formData.targetRole,
    roughStructure: formData.roughStructure,
  };

  let persistedOutlineId: string | null = null;
  let lastAgentError: string | null = null;

  try {
    const iterator: AsyncGenerator<OutlineSseEvent> = streamGenerate(
      request,
      controller.signal,
    );
    for await (const ev of iterator) {
      if (ev.type === 'progress') {
        useOutlineGenerationStore.setState({ statusMessage: ev.message || 'Working…' });
      } else if (ev.type === 'partial') {
        useOutlineGenerationStore.setState({ statusMessage: 'Drafting modules…' });
      } else if (ev.type === 'complete') {
        useOutlineGenerationStore.setState({ statusMessage: 'Saving…' });
      } else if (ev.type === 'persisted') {
        persistedOutlineId = ev.outline.id;
      } else if (ev.type === 'error') {
        lastAgentError = ev.message;
      }
    }
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Cancellation flagged the store already; nothing else to do.
      return;
    }
    const message = err instanceof Error ? err.message : 'Generation failed';
    let code: OutlineGenerationErrorCode = 'OTHER';
    if (/timeout|timed out/i.test(message)) code = 'TIMEOUT';
    else if (/closed|connection|network|fetch/i.test(message)) code = 'CONNECTION';
    useOutlineGenerationStore.setState({
      status: 'failed',
      statusMessage: null,
      errorMessage: message,
      errorCode: code,
    });
    return;
  } finally {
    if (activeController === controller) activeController = null;
  }

  // Stream ended cleanly. Classify outcome.
  if (persistedOutlineId) {
    useOutlineGenerationStore.setState((s) => ({
      status: 'completed',
      statusMessage: null,
      errorMessage: null,
      errorCode: null,
      lastPersistedOutlineId: persistedOutlineId,
      lastPersistedTopic: formData.topic,
      persistedTick: s.persistedTick + 1,
    }));
  } else {
    // Either an agent error event, or the stream ended without persisting.
    useOutlineGenerationStore.setState({
      status: 'failed',
      statusMessage: null,
      errorMessage:
        lastAgentError || 'Generation finished without saving an outline.',
      errorCode: lastAgentError ? 'AGENT' : 'OTHER',
    });
  }
}

// =============================================================================
// Store
// =============================================================================

export const useOutlineGenerationStore = create<OutlineGenerationState>(
  (set, get) => ({
    status: 'idle',
    topic: null,
    statusMessage: null,
    errorMessage: null,
    errorCode: null,
    retryCount: 0,
    formData: null,
    persistedTick: 0,
    lastPersistedOutlineId: null,
    lastPersistedTopic: null,

    start: (formData) => {
      if (get().status === 'streaming') {
        toast.info('Another outline generation is already in progress.');
        return;
      }
      set({
        status: 'streaming',
        topic: formData.topic,
        statusMessage: 'Starting…',
        errorMessage: null,
        errorCode: null,
        formData,
        retryCount: 0,
      });
      toast.info(`Generating outline for "${formData.topic}". You can keep using the app — we'll let you know when it's ready.`);
      void runOutlineSse(formData);
    },

    cancel: () => {
      activeController?.abort();
      activeController = null;
      set({
        status: 'cancelled',
        statusMessage: null,
      });
      toast.info('Outline generation cancelled');
    },

    retry: () => {
      const { formData, retryCount, errorCode } = get();
      if (!formData) return;
      if (errorCode === 'AGENT') {
        // Agent rejected the input — retrying with the same params would
        // probably fail the same way. Let the user edit instead.
        toast.error('The AI rejected this prompt. Try editing your inputs.');
        return;
      }
      const nextAttempt = retryCount + 1;
      if (nextAttempt > MAX_RETRY_ATTEMPTS) {
        toast.error(`Already retried ${MAX_RETRY_ATTEMPTS} times. Try editing your inputs.`);
        return;
      }
      set({
        status: 'streaming',
        statusMessage: 'Retrying…',
        errorMessage: null,
        errorCode: null,
        retryCount: nextAttempt,
      });
      void runOutlineSse(formData);
    },

    dismiss: () => {
      set({
        status: 'idle',
        topic: null,
        statusMessage: null,
        errorMessage: null,
        errorCode: null,
        formData: null,
      });
    },
  }),
);

// =============================================================================
// Selectors
// =============================================================================

export const selectIsOutlineGenerating = (s: OutlineGenerationState) =>
  s.status === 'streaming';
